import { type ReactNode, type RefCallback, type RefObject } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import type { ItemDragAndDropGroup, NavItemConfigWithCustomizationOptions } from '../../types';
import {
	createL1ItemData,
	getL1ItemData,
	type IsItemDraggable,
	type IsItemDroppable,
	isL1ItemData,
	isL1ItemDataInGroup,
	type TL1ItemData,
} from '../dnd-data';

import { useItemDragAndDrop } from './index';

interface L1ItemDragAndDrop {
	draggableRef: RefCallback<HTMLAnchorElement | HTMLButtonElement> | undefined;
	dropIndicator: ReactNode | undefined;
	dropTargetRef: RefObject<HTMLDivElement> | undefined;
	isDragging: boolean;
	isItemDraggable: boolean;
	isItemDroppable: boolean;
	l1DragAndDropEnabled?: boolean;
	maybeDragPreview: ReactNode;
}

type UseL1ItemDragAndDrop = (args: {
	group: ItemDragAndDropGroup;
	id: string;
	isExpandable: boolean;
	isExpandOrFlyoutOpen: boolean;
	item: NavItemConfigWithCustomizationOptions;
	l1DragAndDropEnabled?: boolean;
	onExpandFlyoutToggle: (open: boolean) => void;
	peek: boolean;
}) => L1ItemDragAndDrop;

export const useL1ItemDragAndDrop: UseL1ItemDragAndDrop = ({
	item,
	id,
	isExpandable,
	isExpandOrFlyoutOpen,
	onExpandFlyoutToggle,
	peek,
	l1DragAndDropEnabled: l1DragAndDropEnabledProp,
	group,
}) => {
	const l1DragAndDropEnabled = l1DragAndDropEnabledProp && !fg('navx-gsn-dnc-disable-dnd');
	const baseData = {
		menuItemId: id,
		group,
		isHidden: false,
		isHideable: item.isHideable !== false,
		label: item.label,
		isReorderable: item.isReorderable !== false,
	};
	const data = createL1ItemData(baseData);
	const isItemDraggable: IsItemDraggable<TL1ItemData> = () => {
		return (
			!!l1DragAndDropEnabled &&
			!peek &&
			(item.isHideable !== false || item.isReorderable !== false) &&
			(item.type !== 'custom' || !!item.isDraggable)
		);
	};

	const isItemDroppable: IsItemDroppable<TL1ItemData> = () =>
		!!l1DragAndDropEnabled && !peek && (item.type !== 'custom' || !!item.isDraggable);

	return useItemDragAndDrop<TL1ItemData>({
		data,
		getItemData: getL1ItemData,
		group,
		id,
		item,
		isDragAndDropEnabled: l1DragAndDropEnabled,
		isExpandable,
		isExpandOrFlyoutOpen,
		isItemData: isL1ItemData,
		isItemDataInGroup: isL1ItemDataInGroup,
		isItemDraggable,
		isItemDroppable,
		onExpandFlyoutToggle,
	});
};
